/**
 * SaveIndicator - Persistent sync status indicator
 * Shows current save state in the header per spec Section 6
 *
 * States:
 * - Saved: "Saved ✓" green, fades after 2 seconds
 * - Saving: "Saving..." gray with spinner
 * - Offline: "Offline - Queued (N)" yellow, persistent
 * - Failed: "Save Failed" red with retry
 */

import { useSync, SyncState } from '../context/SyncContext'

function SaveIndicator() {
  const { syncState, queuedCount, retry, isSaving, isSaved, isOffline, isFailed } = useSync()

  // Don't show anything in idle state
  if (syncState === SyncState.IDLE) {
    return null
  }

  return (
    <div
      className={`
        flex items-center gap-1.5 text-xs font-medium
        transition-opacity duration-300
        ${isSaved ? 'animate-fade-out' : ''}
      `}
    >
      {/* Saved State */}
      {isSaved && (
        <>
          <span className="text-status-green">✓</span>
          <span className="text-status-green">Saved</span>
        </>
      )}

      {/* Saving State */}
      {isSaving && (
        <>
          <span className="animate-spin inline-block w-3 h-3 border-2 border-gray-400 border-t-transparent rounded-full" />
          <span className="text-gray-400">Saving...</span>
        </>
      )}

      {/* Offline State */}
      {isOffline && (
        <>
          <span className="text-status-yellow">⚠</span>
          <span className="text-status-yellow">
            Offline{queuedCount > 0 && ` - Queued (${queuedCount})`}
          </span>
        </>
      )}

      {/* Failed State */}
      {isFailed && (
        <>
          <span className="text-status-red">✕</span>
          <span className="text-status-red">Save Failed</span>
          <button
            onClick={retry}
            className="ml-1 underline hover:no-underline text-status-red"
          >
            Retry
          </button>
        </>
      )}
    </div>
  )
}

export default SaveIndicator
